import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  User,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import PhoneInput from "react-phone-number-input/input";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import ReactCountryFlag from "react-country-flag";
import { useForm } from "react-hook-form";
import { useAuth } from "../contexts/AuthContext";
import { authService } from "../services";

const Register = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // PIN states
  const [pin, setPin] = useState(["", "", "", ""]);
  const [confirmPin, setConfirmPin] = useState(["", "", "", ""]);

  // Store temporary token
  const [registerToken, setRegisterToken] = useState(null);

  // Refs for auto-focus
  const pinRefs = useRef([]);
  const confirmPinRefs = useRef([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    setError: setFieldError,
    clearErrors,
    getValues,
  } = useForm({
    mode: "onBlur",
  });

  const phoneValue = watch("phone");

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Validate Ghana phone number
  const validatePhoneNumber = (phone) => {
    if (!phone) {
      return "Phone number is required";
    }

    try {
      if (phone.startsWith("+233")) {
        const phoneNumber = parsePhoneNumber(phone);
        if (phoneNumber?.country === "GH" && isValidPhoneNumber(phone)) {
          return true;
        }
      } else if (phone.startsWith("0") && phone.length === 10) {
        const ghanaPhoneRegex = /^0[2-5][0-9]{8}$/;
        if (ghanaPhoneRegex.test(phone)) {
          return true;
        }
      }
      return "Please enter a valid Ghana phone number";
    } catch (error) {
      return "Invalid phone number format";
    }
  };

  // Format phone for API
  const formatPhoneForAPI = (phone) => {
    if (phone?.startsWith("+233")) {
      return "0" + phone.substring(4);
    }
    return phone;
  };

  // Handle phone input change
  const handlePhoneChange = (value) => {
    setValue("phone", value);
    if (errors.phone) {
      clearErrors("phone");
    }
  };

  // Handle phone blur
  const handlePhoneBlur = () => {
    const phoneValue = getValues("phone");
    if (phoneValue) {
      const validation = validatePhoneNumber(phoneValue);
      if (validation !== true) {
        setFieldError("phone", { type: "manual", message: validation });
      }
    }
  };

  // Handle PIN input
  const handlePinChange = (index, value, isConfirm = false) => {
    const digit = value.replace(/[^\d]/g, "").slice(-1);

    if (isConfirm) {
      const newConfirmPin = [...confirmPin];
      newConfirmPin[index] = digit;
      setConfirmPin(newConfirmPin);

      if (digit && index < 3) {
        confirmPinRefs.current[index + 1]?.focus();
      }
    } else {
      const newPin = [...pin];
      newPin[index] = digit;
      setPin(newPin);

      if (digit && index < 3) {
        pinRefs.current[index + 1]?.focus();
      }
    }
  };

  // Handle PIN backspace
  const handlePinKeyDown = (index, e, isConfirm = false) => {
    if (e.key === "Backspace") {
      const refs = isConfirm ? confirmPinRefs : pinRefs;

      if (isConfirm) {
        const newConfirmPin = [...confirmPin];
        if (!newConfirmPin[index] && index > 0) {
          refs.current[index - 1]?.focus();
        } else {
          newConfirmPin[index] = "";
          setConfirmPin(newConfirmPin);
        }
      } else {
        const newPin = [...pin];
        if (!newPin[index] && index > 0) {
          refs.current[index - 1]?.focus();
        } else {
          newPin[index] = "";
          setPin(newPin);
        }
      }
    }
  };

  // Step 1: Basic Info - Send OTP
  const handleStep1Submit = async (data) => {
    setLoading(true);
    setError("");

    const phoneValidation = validatePhoneNumber(data.phone);
    if (phoneValidation !== true) {
      setFieldError("phone", { type: "manual", message: phoneValidation });
      setLoading(false);
      return;
    }

    try {
      const formattedPhone = formatPhoneForAPI(data.phone);

      const response = await authService.register({
        first_name: data.firstName,
        last_name: data.lastName,
        phone: formattedPhone,
      });

      if (response.status === "success") {
        setRegisterToken(response.data.token);
        localStorage.setItem("register_token", response.data.token);
        // Skip OTP, go directly to PIN setup
        setCurrentStep(2);
      } else {
        setError(response.message || "Registration failed");
      }
    } catch (err) {
      console.error("Registration error:", err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Set PIN and complete registration
  const handleStep2Submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const pinString = pin.join("");
    const confirmPinString = confirmPin.join("");

    if (pinString.length !== 4) {
      setError("PIN must be 4 digits");
      setLoading(false);
      return;
    }

    if (pinString !== confirmPinString) {
      setError("PINs do not match");
      setLoading(false);
      return;
    }

    try {
      const token = registerToken || localStorage.getItem("register_token");

      const response = await authService.setPin(
        {
          pin: pinString,
          confirm_pin: confirmPinString,
        },
        token,
      );

      if (response.status === "success") {
        login(response.data.tokens, response.data.user);
        navigate("/dashboard", { replace: true });
      } else {
        setError(response.message || "Registration completion failed");
      }
    } catch (err) {
      console.error("Registration completion error:", err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Registration completion failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Go back function
  const goBack = (e) => {
    e.preventDefault();
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError("");

      // Clear step-specific data when going back
      if (currentStep === 2) {
        setPin(["", "", "", ""]);
        setConfirmPin(["", "", "", ""]);
      }
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return "Create account";
      case 2:
        return "Secure your account";
      default:
        return "Create account";
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 1:
        return "Let's get you started";
      case 2:
        return "Create a secure PIN for your account";
      default:
        return "Let's get you started";
    }
  };

  // Check if step 1 form is valid
  const isStep1Valid = () => {
    const values = getValues();
    return (
      values.firstName &&
      values.firstName.length >= 2 &&
      values.lastName &&
      values.lastName.length >= 2 &&
      values.phone &&
      validatePhoneNumber(values.phone) === true
    );
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Side - Hidden on mobile */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{ background: "#FFD700" }}
      >
        <div className="relative z-10 flex flex-col justify-start items-start text-black p-12 w-full">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-1 text-black">
              Cinivest <b className="text-white">Pro</b>{" "}
              <small className="text-sm">v1.0</small>
            </h1>
            <p className="text-xl text-black max-w-md font-light">
              Join thousands of successful agents
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 right-0">
          <img
            src="/customers.png"
            alt="Happy customers"
            className="h-80 w-auto object-contain"
            style={{
              filter: "drop-shadow(0 0 0 transparent)",
              background: "transparent",
            }}
          />
        </div>
      </div>

      {/* Right Side - Registration Wizard */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div className="lg:hidden text-center mb-8">
            <img
              src="/logo.jpg"
              alt="Cinivest Systems"
              className="h-20 w-auto mx-auto rounded-full"
            />
          </div>

          {/* Step Indicator */}
          <div className="flex justify-center mb-8">
            <div className="flex space-x-2">
              {[1, 2].map((step) => (
                <div
                  key={step}
                  className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                    step <= currentStep ? "bg-yellow-400" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-gray-900">
              {getStepTitle()}
            </h2>
            <p className="mt-2 text-sm text-gray-600">{getStepDescription()}</p>
          </div>

          <div className="mt-8">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50  border-red-200 rounded-2xl text-center">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Step 1: Basic Info Form */}
            {currentStep === 1 && (
              <form
                className="space-y-6"
                onSubmit={handleSubmit(handleStep1Submit)}
              >
                {/* First Name and Last Name */}
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      First Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="firstName"
                        {...register("firstName", {
                          required: "First name is required",
                          pattern: {
                            value: /^[a-zA-Z\s]{2,50}$/,
                            message:
                              "Name must contain only letters (2-50 characters)",
                          },
                        })}
                        disabled={loading}
                        className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200 transition duration-200 ease-in-out sm:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                        placeholder="First name"
                      />
                    </div>
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>

                  <div className="flex-1">
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Last Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="lastName"
                        {...register("lastName", {
                          required: "Last name is required",
                          pattern: {
                            value: /^[a-zA-Z\s]{2,50}$/,
                            message:
                              "Name must contain only letters (2-50 characters)",
                          },
                        })}
                        disabled={loading}
                        className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200 transition duration-200 ease-in-out sm:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                        placeholder="Last name"
                      />
                    </div>
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Phone Number */}
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Phone Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <ReactCountryFlag
                        countryCode="GH"
                        svg
                        style={{ width: "1.5em", height: "1.5em" }}
                      />
                    </div>
                    <PhoneInput
                      country="GH"
                      value={phoneValue}
                      onChange={handlePhoneChange}
                      onBlur={handlePhoneBlur}
                      disabled={loading}
                      className="appearance-none block w-full pl-12 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200 transition duration-200 ease-in-out sm:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="024 512 3456"
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.phone.message}
                    </p>
                  )}
                </div>

                {/* Continue Button */}
                <div>
                  <button
                    type="submit"
                    disabled={loading || !isStep1Valid()}
                    className="w-full flex justify-center items-center py-3 px-4  border-transparent text-base font-medium rounded-lg text-black shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 transition duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-yellow-400 to-yellow-600"
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                        Registering...
                      </div>
                    ) : (
                      <>
                        Continue <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: PIN Creation */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-sm text-gray-600">
                    Almost there! Set up your PIN to secure your account.
                  </p>
                </div>

                <form onSubmit={handleStep2Submit}>
                  {/* PIN Creation */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Create PIN (4 digits)
                      </label>
                      <button
                        type="button"
                        disabled={loading}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none transition ease-in-out duration-150 disabled:cursor-not-allowed"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>

                    <div className="flex justify-center space-x-4 mb-6">
                      {[0, 1, 2, 3].map((index) => (
                        <div key={index} className="relative">
                          <input
                            ref={(el) => (pinRefs.current[index] = el)}
                            type={showPassword ? "text" : "password"}
                            maxLength="1"
                            value={pin[index]}
                            onChange={(e) =>
                              handlePinChange(index, e.target.value)
                            }
                            onKeyDown={(e) => handlePinKeyDown(index, e)}
                            disabled={loading}
                            className="w-16 h-16 text-center text-xl font-bold bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
                          />
                          {!showPassword && pin[index] && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="w-3 h-3 bg-gray-800 rounded-full"></div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Confirm PIN */}
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-4">
                      Confirm PIN
                    </label>
                    <div className="flex justify-center space-x-4">
                      {[0, 1, 2, 3].map((index) => (
                        <div key={index} className="relative">
                          <input
                            ref={(el) => (confirmPinRefs.current[index] = el)}
                            type={showPassword ? "text" : "password"}
                            maxLength="1"
                            value={confirmPin[index]}
                            onChange={(e) =>
                              handlePinChange(index, e.target.value, true)
                            }
                            onKeyDown={(e) => handlePinKeyDown(index, e, true)}
                            disabled={loading}
                            className="w-16 h-16 text-center text-xl font-bold bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
                          />
                          {!showPassword && confirmPin[index] && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="w-3 h-3 bg-gray-800 rounded-full"></div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={goBack}
                      disabled={loading}
                      className="flex-1 flex justify-center items-center py-3 px-4 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 transition duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowLeft className="mr-2 h-5 w-5" /> Back
                    </button>
                    <button
                      type="submit"
                      disabled={
                        loading ||
                        pin.join("").length !== 4 ||
                        confirmPin.join("").length !== 4
                      }
                      className="flex-1 flex justify-center items-center py-3 px-4 border border-transparent text-base font-medium rounded-lg text-black shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 transition duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-yellow-400 to-yellow-600"
                    >
                      {loading ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                          Completing...
                        </div>
                      ) : (
                        <>
                          Submit <ArrowRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Login Link - Only show on step 1 */}
            {currentStep === 1 && (
              <div className="text-center mt-6">
                <p className="text-sm text-gray-600">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="font-medium hover:underline transition duration-150 ease-in-out text-yellow-600"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
